import React, { useMemo } from 'react';

const PasswordStrengthMeter = ({ password }) => {
  const requirements = useMemo(() => {
    return [
      {
        label: 'Minimal 8 karakter',
        met: password.length >= 8
      },
      {
        label: 'Huruf besar (A-Z)',
        met: /[A-Z]/.test(password)
      },
      {
        label: 'Huruf kecil (a-z)',
        met: /[a-z]/.test(password)
      },
      {
        label: 'Angka (0-9)',
        met: /\d/.test(password)
      },
      {
        label: 'Karakter spesial (!@#$%^&*)',
        met: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
      }
    ];
  }, [password]);

  const metCount = requirements.filter(r => r.met).length;
  const strength = metCount === 0 ? 0 : (metCount / requirements.length) * 100;

  const getStrengthLabel = () => {
    if (metCount === 0) return { label: '', color: 'bg-gray-200' };
    if (metCount <= 2) return { label: 'Lemah', color: 'bg-red-500' };
    if (metCount <= 3) return { label: 'Sedang', color: 'bg-yellow-500' };
    if (metCount <= 4) return { label: 'Kuat', color: 'bg-blue-500' };
    return { label: 'Sangat Kuat', color: 'bg-green-500' };
  };

  const { label, color } = getStrengthLabel();

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${strength}%` }}
          />
        </div>
        {label && (
          <span className={`text-xs font-medium ${
            metCount <= 2 ? 'text-red-600' :
            metCount <= 3 ? 'text-yellow-600' :
            metCount <= 4 ? 'text-blue-600' :
            'text-green-600'
          }`}>
            {label}
          </span>
        )}
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-1 gap-1">
        {requirements.map((req, index) => (
          <div
            key={index}
            className={`flex items-center gap-2 text-xs ${
              req.met ? 'text-green-600' : 'text-gray-500'
            }`}
          >
            {req.met ? (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
              </svg>
            )}
            <span>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const isPasswordValid = (password) => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
  );
};

export default PasswordStrengthMeter;
